import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const LANGS = [
  { code: 'es', flag: '\uD83C\uDDEA\uD83C\uDDF8', name: 'Espanol' },
  { code: 'en', flag: '\uD83C\uDDEC\uD83C\uDDE7', name: 'English' },
  { code: 'fr', flag: '\uD83C\uDDEB\uD83C\uDDF7', name: 'Francais' },
  { code: 'de', flag: '\uD83C\uDDE9\uD83C\uDDEA', name: 'Deutsch' },
  { code: 'it', flag: '\uD83C\uDDEE\uD83C\uDDF9', name: 'Italiano' },
  { code: 'pt', flag: '\uD83C\uDDF5\uD83C\uDDF9', name: 'Portugues' },
  { code: 'ja', flag: '\uD83C\uDDEF\uD83C\uDDF5', name: 'Japanese' },
  { code: 'ko', flag: '\uD83C\uDDF0\uD83C\uDDF7', name: 'Korean' },
  { code: 'zh', flag: '\uD83C\uDDE8\uD83C\uDDF3', name: 'Chinese' },
  { code: 'ru', flag: '\uD83C\uDDF7\uD83C\uDDFA', name: 'Russian' },
  { code: 'ar', flag: '\uD83C\uDDF8\uD83C\uDDE6', name: 'Arabic' },
  { code: 'nl', flag: '\uD83C\uDDF3\uD83C\uDDF1', name: 'Nederlands' },
];

// Clean email body: remove forwarded headers, signatures, HTML entities, quotes
function cleanQuestion(subject, body) {
  if (!body && !subject) return '';
  let text = body || subject || '';
  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&#\d+;/g, '');
  // Remove forwarded message headers
  text = text.replace(/[-]{3,}\s*Forwarded message\s*[-]{3,}[\s\S]*?Subject:[^\n]*/gi, '');
  text = text.replace(/^From:.*$/gm, '').replace(/^To:.*$/gm, '').replace(/^Date:.*$/gm, '').replace(/^Sent:.*$/gm, '');
  // Remove email signatures (common patterns)
  text = text.replace(/(Regards|Saludos|Cordialement|Best regards|Kind regards|Atentamente|Cordialmente|Mit freundlichen|Distinti saluti)[\s\S]*$/i, '');
  // Remove quoted replies
  text = text.replace(/^>.*$/gm, '');
  text = text.replace(/El .* escribi[oó]:/g, '');
  text = text.replace(/On .* wrote:/g, '');
  // Clean up whitespace
  text = text.replace(/\n{3,}/g, '\n\n').trim();
  // If still too long, truncate
  if (text.length > 500) text = text.substring(0, 500) + '...';
  return text;
}

function AlcazarReenviadas() {
  const { user } = useAuth();
  const isEditor = user && ['superadmin', 'admin', 'revisor'].includes(user.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showResolved, setShowResolved] = useState(false);
  const [toast, setToast] = useState(null);
  const pageSize = 20;

  // Resolve form state
  const [resolvingId, setResolvingId] = useState(null);
  const [existingTopics, setExistingTopics] = useState([]);

  // Fetch existing KB topics for selector
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const res = await api.get('/api/alcazar/knowledge', { params: { lang: 'es' } });
        if (res.data.success) {
          setExistingTopics((res.data.topics || []).map(t => t.topic).sort());
        }
      } catch {}
    };
    fetchTopics();
  }, []);
  const [resolveTopic, setResolveTopic] = useState('');
  const [resolveContent, setResolveContent] = useState('');
  const [resolveFaq, setResolveFaq] = useState('');
  const [resolveLang, setResolveLang] = useState('es');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [translatedQ, setTranslatedQ] = useState({});
  const [translatingQ, setTranslatingQ] = useState(null);

  const translateQuestion = async (id, text) => {
    setTranslatingQ(id);
    try {
      const res = await api.post('/api/alcazar/translate-text', { text, target_lang: 'es' });
      if (res.data.success) {
        setTranslatedQ(prev => ({ ...prev, [id]: res.data.translated }));
      }
    } catch (err) {
      showToast('Error al traducir', 'error');
    } finally {
      setTranslatingQ(null);
    }
  };

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/alcazar/reenviadas', {
        params: { show_resolved: showResolved ? '1' : '0' }
      });
      if (res.data.success) {
        setItems(res.data.items || []);
      }
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) return;
      setError('Error al cargar reenviadas');
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.subject || '').toLowerCase().includes(q) ||
      (i.email_body || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [search, showResolved]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const startResolve = (item) => {
    setResolvingId(item.id);
    setResolveTopic('');
    setResolveContent('');
    setResolveFaq(item.subject || '');
    setResolveLang(item.detected_language || 'es');
  };

  const cancelResolve = () => {
    setResolvingId(null);
    setResolveTopic('');
    setResolveContent('');
    setResolveFaq('');
  };

  const submitResolve = async () => {
    if (!resolveTopic.trim() || !resolveContent.trim()) {
      showToast('Topic y contenido son obligatorios', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/api/alcazar/reenviadas/${resolvingId}/resolver`, {
        topic: resolveTopic.trim(),
        content: resolveContent.trim(),
        faq_question: resolveFaq.trim(),
        language: resolveLang,
      });
      if (res.data.success) {
        showToast(`Guardado en Conocimiento: ${resolveTopic.trim()}`);
        cancelResolve();
        fetchItems();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const pendingCount = items.filter(i => !i.resolved).length;

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <strong>Reenviadas:</strong> Preguntas que XIA no supo responder. Haz clic en el lapiz para escribir la respuesta correcta y guardarla en Conocimiento. XIA aprendera automaticamente.
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-white font-medium ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Buscar por pregunta..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={e => setShowResolved(e.target.checked)}
              className="rounded border-gray-300"
            />
            Mostrar resueltas
          </label>
          <span className="text-sm text-gray-500">
            {pendingCount} pendientes
          </span>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-3 text-gray-500">Cargando...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Fecha</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Canal</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pregunta</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24">Acciones</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-28">Estado</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No hay reenviadas{showResolved ? '' : ' pendientes'}</td></tr>
                ) : pageItems.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    {/* Row 1: Subject / question title */}
                    <tr className={`hover:bg-gray-50 border-t border-gray-100 ${item.resolved ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-1.5 text-xs text-gray-400 align-middle" rowSpan={resolvingId === item.id ? 1 : 2}>{(page - 1) * pageSize + idx + 1}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap align-middle" rowSpan={resolvingId === item.id ? 1 : 2}>{formatDate(item.timestamp)}</td>
                      <td className="px-3 py-1.5 align-middle" rowSpan={resolvingId === item.id ? 1 : 2}>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          item.channel === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>{item.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}</span>
                      </td>
                      <td className="px-3 py-1.5 text-sm font-semibold text-gray-800">
                        {item.subject || <span className="text-gray-400 italic">Sin asunto</span>}

                      </td>
                      <td className="px-3 py-1.5 text-center align-middle" rowSpan={resolvingId === item.id ? 1 : 2}>
                        <div className="flex items-center justify-center gap-1">
                          {isEditor && !item.resolved && (
                            <button onClick={() => startResolve(item)} title="Resolver: escribir respuesta"
                              className="p-1 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-center align-middle" rowSpan={resolvingId === item.id ? 1 : 2}>
                        {item.resolved ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-green-500">&#10003;</span>
                            <span className="text-xs text-gray-500 truncate max-w-[70px]">{item.resolved_topic || 'Resuelta'}</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-gray-300">&#10003;</span>
                            <span className="text-xs text-gray-400">Pendiente</span>
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* Row 2: Click to expand full question + translate */}
                    {resolvingId !== item.id && (
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                        <td className="px-3 pb-2 text-xs text-gray-500 leading-relaxed" colSpan={1}>
                          {expandedId === item.id ? (
                            <div>
                              <div className="text-sm text-gray-700 whitespace-pre-wrap mb-2">
                                {cleanQuestion(item.subject, item.email_body) || 'Sin contenido'}
                              </div>
                              {!translatedQ[item.id] ? (
                                <button onClick={(e) => { e.stopPropagation(); translateQuestion(item.id, item.email_body || item.subject); }}
                                  disabled={translatingQ === item.id}
                                  className="px-3 py-1.5 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-lg text-xs hover:bg-yellow-200 disabled:opacity-50">
                                  {translatingQ === item.id ? 'Traduciendo...' : 'Traducir al espanol'}
                                </button>
                              ) : (
                                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                                  <span className="text-xs font-semibold text-yellow-700 uppercase block mb-1">Traduccion:</span>
                                  {translatedQ[item.id]}
                                </div>
                              )}
                            </div>
                          ) : (
                            cleanQuestion(item.subject, item.email_body) ? (
                              cleanQuestion(item.subject, item.email_body).length > 150
                                ? cleanQuestion(item.subject, item.email_body).substring(0, 150) + '...'
                                : cleanQuestion(item.subject, item.email_body)
                            ) : (
                              <span className="text-gray-400 italic">Sin contenido</span>
                            )
                          )}
                        </td>
                      </tr>
                    )}

                    {/* Resolve form */}
                    {resolvingId === item.id && (
                      <tr>
                        <td colSpan={5} className="px-3 pb-3" onKeyDown={e => e.stopPropagation()}>
                          <div className="bg-green-50 rounded-lg p-4 border border-green-200 space-y-3">
                            <div className="text-sm font-semibold text-green-800 mb-2">Anadir respuesta a Conocimiento</div>

                            {/* Full original question */}
                            <div className="bg-white rounded p-3 text-sm text-gray-700 border whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                              {cleanQuestion(item.subject, item.email_body) || 'Sin contenido'}
                            </div>
                            {!translatedQ[item.id] ? (
                              <button onClick={() => translateQuestion(item.id, item.email_body || item.subject)}
                                disabled={translatingQ === item.id}
                                className="mt-2 px-3 py-1.5 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-lg text-xs hover:bg-yellow-200 disabled:opacity-50">
                                {translatingQ === item.id ? 'Traduciendo...' : 'Traducir al espanol'}
                              </button>
                            ) : (
                              <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                                <span className="text-xs font-semibold text-yellow-700 uppercase block mb-1">Traduccion:</span>
                                {translatedQ[item.id]}
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Topic</label>
                                <select
                                  value={resolveTopic}
                                  onChange={e => setResolveTopic(e.target.value)}
                                  className="w-full border border-green-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 bg-white"
                                >
                                  <option value="">-- Seleccionar topic existente o crear nuevo --</option>
                                  <option value="__nuevo__">+ Crear topic nuevo...</option>
                                  {existingTopics.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                                {resolveTopic === '__nuevo__' && (
                                  <input
                                    type="text"
                                    onChange={e => setResolveTopic(e.target.value === '' ? '__nuevo__' : e.target.value)}
                                    placeholder="nombre_nuevo_topic (sin espacios, con guion bajo)"
                                    className="w-full border border-green-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 mt-2"
                                  />
                                )}
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Idioma</label>
                                <select value={resolveLang} onChange={e => setResolveLang(e.target.value)}
                                  className="w-full border border-green-300 rounded-lg px-3 py-1.5 text-sm bg-white">
                                  {LANGS.map(l => (<option key={l.code} value={l.code}>{l.flag} {l.name}</option>))}
                                </select>
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Pregunta para guardar en conocimiento</label>
                              <input
                                type="text"
                                value={resolveFaq}
                                onChange={e => setResolveFaq(e.target.value)}
                                className="w-full border border-green-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Respuesta correcta</label>
                              <textarea
                                value={resolveContent}
                                onChange={e => setResolveContent(e.target.value)}
                                placeholder="Escribe la respuesta que XIA debe dar a partir de ahora..."
                                className="w-full border border-green-300 rounded-lg p-3 text-sm min-h-[120px] focus:ring-2 focus:ring-green-500"
                              />
                            </div>

                            <div className="flex gap-2">
                              <button onClick={submitResolve} disabled={saving}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition">
                                {saving ? 'Guardando...' : 'Guardar en Conocimiento + Traducir a todos los idiomas'}
                              </button>
                              <button onClick={cancelResolve}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition">
                                Cancelar
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Pagina {page} de {totalPages} ({filtered.length} reenviadas)
              </span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50">
                  Anterior
                </button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`px-3 py-1 text-sm border rounded ${
                        p === page ? 'bg-amber-600 text-white' : 'bg-white hover:bg-gray-50'
                      }`}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50">
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AlcazarReenviadas;
